// =============================================================================
// MeuDriver — Admin Coupons Management
// =============================================================================

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Plus, Tag, Trash2, Loader2, Check, Copy } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatCurrency } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CouponRow {
  id: string
  code: string
  discount_type: 'percent' | 'fixed'
  discount_value: number
  min_order: number | null
  max_uses: number | null
  uses_count: number
  expires_at: string | null
  is_active: boolean
  instructor_id: string | null
  city: string | null
  created_at: string
}

interface InstructorOption {
  id: string
  full_name: string
}

interface CouponForm {
  code: string
  discount_type: 'percent' | 'fixed'
  discount_value: string
  min_order: string
  max_uses: string
  expires_at: string
  instructor_id: string
  city: string
}

const EMPTY_FORM: CouponForm = {
  code: '',
  discount_type: 'percent',
  discount_value: '',
  min_order: '',
  max_uses: '',
  expires_at: '',
  instructor_id: '',
  city: '',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState<CouponRow[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<CouponForm>(EMPTY_FORM)
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [instructors, setInstructors] = useState<InstructorOption[]>([])

  async function fetchCoupons() {
    setLoading(true)
    const { data } = await supabase
      .from('coupons')
      .select('id, code, discount_type, discount_value, min_order, max_uses, uses_count, expires_at, is_active, instructor_id, city, created_at')
      .order('created_at', { ascending: false })
    setCoupons((data ?? []) as CouponRow[])
    setLoading(false)
  }

  async function fetchInstructors() {
    // `is_approved` não existe em `instructors` — a coluna é `status`
    // (enum instructor_status). O filtro antigo fazia o PostgREST devolver
    // erro, deixando a lista de instrutores do cupom sempre vazia.
    const { data } = await supabase
      .from('instructors')
      .select('id, profiles!instructors_profile_id_fkey(full_name)')
      .eq('status', 'approved')
    const mapped = (data ?? []).map((row: any) => ({
      id: row.id as string,
      full_name: (row.profiles as any)?.full_name ?? 'Instrutor',
    }))
    setInstructors(mapped)
  }

  useEffect(() => { fetchCoupons(); fetchInstructors() }, [])

  async function handleCreate() {
    const code = form.code.trim().toUpperCase()
    const value = Number(form.discount_value)
    if (!code) { toast.error('Informe um código para o cupom.'); return }
    if (!value || value <= 0) { toast.error('Informe um valor de desconto válido.'); return }
    if (form.discount_type === 'percent' && (value < 1 || value > 100)) {
      toast.error('Percentual deve ser entre 1 e 100.'); return
    }
    setCreating(true)
    try {
      const { error } = await supabase.from('coupons').insert({
        code,
        discount_type: form.discount_type,
        discount_value: value,
        min_order: form.min_order ? Number(form.min_order) : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59').toISOString() : null,
        is_active: true,
        uses_count: 0,
        instructor_id: form.instructor_id || null,
        city: form.city.trim() || null,
      })
      if (error) throw error
      toast.success(`Cupom ${code} criado!`)
      setDialogOpen(false)
      setForm(EMPTY_FORM)
      await fetchCoupons()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao criar cupom.')
    } finally {
      setCreating(false)
    }
  }

  async function handleToggle(coupon: CouponRow) {
    setTogglingId(coupon.id)
    try {
      await supabase
        .from('coupons')
        .update({ is_active: !coupon.is_active })
        .eq('id', coupon.id)
        .throwOnError()
      setCoupons((prev) =>
        prev.map((c) => c.id === coupon.id ? { ...c, is_active: !c.is_active } : c),
      )
    } catch {
      toast.error('Erro ao alterar status.')
    } finally {
      setTogglingId(null)
    }
  }

  async function handleDelete(id: string, code: string) {
    if (!confirm(`Excluir cupom "${code}"? Esta ação não pode ser desfeita.`)) return
    setDeletingId(id)
    try {
      await supabase.from('coupons').delete().eq('id', id).throwOnError()
      toast.success('Cupom excluído.')
      setCoupons((prev) => prev.filter((c) => c.id !== id))
    } catch {
      toast.error('Erro ao excluir cupom.')
    } finally {
      setDeletingId(null)
    }
  }

  function copyCode(code: string) {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const stats = {
    total: coupons.length,
    active: coupons.filter((c) => c.is_active).length,
    totalUses: coupons.reduce((s, c) => s + c.uses_count, 0),
    expired: coupons.filter((c) => c.expires_at && new Date(c.expires_at) < new Date()).length,
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cupons de desconto</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie cupons promocionais da plataforma.
          </p>
        </div>
        <Button
          onClick={() => { setForm(EMPTY_FORM); setDialogOpen(true) }}
          className="gap-2 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Novo cupom
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total',        value: stats.total },
          { label: 'Ativos',       value: stats.active },
          { label: 'Usos totais',  value: stats.totalUses },
          { label: 'Expirados',    value: stats.expired },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              {loading
                ? <Skeleton className="h-8 w-16 mb-1" />
                : <p className="text-2xl font-bold text-gray-900">{s.value}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4 text-brand-600" />
            Lista de cupons
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : coupons.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <Tag className="h-10 w-10 text-gray-300 mb-3" />
              <p className="font-medium text-gray-500">Nenhum cupom cadastrado</p>
              <p className="text-sm text-muted-foreground mt-1">
                Crie seu primeiro cupom usando o botão acima.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left">
                    <th className="px-4 py-3 font-medium text-muted-foreground">Código</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Desconto</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Min. pedido</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Usos</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Validade</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Ativo</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {coupons.map((coupon) => {
                    const isExpired = coupon.expires_at != null && new Date(coupon.expires_at) < new Date()
                    return (
                      <tr key={coupon.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-brand-700">
                              {coupon.code}
                            </span>
                            <button
                              onClick={() => copyCode(coupon.code)}
                              className="text-gray-400 hover:text-brand-600 transition-colors"
                            >
                              {copiedCode === coupon.code
                                ? <Check className="h-3.5 w-3.5 text-green-600" />
                                : <Copy className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {coupon.discount_type === 'percent'
                            ? `${coupon.discount_value}%`
                            : formatCurrency(coupon.discount_value)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {coupon.min_order != null ? formatCurrency(coupon.min_order) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {coupon.uses_count}
                          {coupon.max_uses != null ? ` / ${coupon.max_uses}` : ''}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {coupon.expires_at ? (
                            <span className={isExpired ? 'text-red-500 font-medium' : ''}>
                              {format(parseISO(coupon.expires_at), 'dd/MM/yyyy', { locale: ptBR })}
                              {isExpired && ' (exp.)'}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <Switch
                            checked={coupon.is_active}
                            disabled={togglingId === coupon.id}
                            onCheckedChange={() => handleToggle(coupon)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50"
                            disabled={deletingId === coupon.id}
                            onClick={() => handleDelete(coupon.id, coupon.code)}
                          >
                            {deletingId === coupon.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-brand-600" />
              Novo cupom
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Code */}
            <div className="space-y-1.5">
              <Label htmlFor="coupon-code">Código</Label>
              <div className="flex gap-2">
                <Input
                  id="coupon-code"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="Ex: PRIMEIRAULA"
                  className="font-mono uppercase"
                  maxLength={20}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setForm((f) => ({ ...f, code: generateCode() }))}
                >
                  Gerar
                </Button>
              </div>
            </div>

            {/* Type + Value */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo de desconto</Label>
                <Select
                  value={form.discount_type}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, discount_type: v as 'percent' | 'fixed' }))
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentual (%)</SelectItem>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Valor</Label>
                <Input
                  type="number"
                  min={1}
                  max={form.discount_type === 'percent' ? 100 : undefined}
                  placeholder={form.discount_type === 'percent' ? '10' : '20'}
                  value={form.discount_value}
                  onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                />
              </div>
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Pedido mínimo (R$)</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Sem mínimo"
                  value={form.min_order}
                  onChange={(e) => setForm((f) => ({ ...f, min_order: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Máx. de usos</Label>
                <Input
                  type="number"
                  min={1}
                  placeholder="Ilimitado"
                  value={form.max_uses}
                  onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
                />
              </div>
            </div>

            {/* Expiry */}
            <div className="space-y-1.5">
              <Label>Data de validade (opcional)</Label>
              <Input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
              />
            </div>

            {/* Segmentation */}
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Segmentação (opcional)
              </p>
              <div className="space-y-1.5">
                <Label>Restringir a um instrutor</Label>
                <Select
                  value={form.instructor_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, instructor_id: v === '_all' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os instrutores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos os instrutores</SelectItem>
                    {instructors.map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Restringir a uma cidade</Label>
                <Input
                  placeholder="Ex: São Paulo"
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Criar cupom
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
